import { useFormikContext } from 'formik';
import { Text, TextElementTypes } from '@picsart/text';
import { Button, ButtonSizeXL, ButtonVariants, ButtonSkinSecondary } from '@picsart/button';
import { useTranslation } from 'react-i18next';
import { Typography, FontWeights } from '@picsart/cascade';

import { IValues } from '../../../utils/common/types';
import Result from '../../resultsSections/AiWriterResult';
import LoadingResults from '../../resultsSections/LoadingResults';
import NoResults from '../../resultsSections/NoResults';
import { getHideGenerateMore, getShowLoadingResults } from '../../../utils/common/category';
import useStore from '../../../services/store';
import useGenerateMoreResults from '../../../utils/hooks/useGenerateMoreResults';
import useRephrase from '../../../utils/hooks/useRephrase';
import { IFormSection } from '../../formInputs/commonTypes';

import useStyles from './styles';

export interface IGeneratedResults extends IFormSection {
  selectedCategory: number;
  editable?: boolean;
  canBeSavedToRecent?: boolean;
}

function Results({
  selectedCategory,
  editable,
  canBeSavedToRecent: canBeSavedToRecents,
}: IGeneratedResults) {
  const { t } = useTranslation();
  const formProps = useFormikContext<IValues>();
  const generateMoreResults = useGenerateMoreResults({
    formProps,
    category: selectedCategory,
  });
  const rephrase = useRephrase({
    formProps,
    category: selectedCategory,
  });
  const loading = useStore((state) => state.resultsAreGenerating);
  const hideGenerateMoreBtn = getHideGenerateMore(selectedCategory);
  const showLoadingResults = getShowLoadingResults(selectedCategory);
  const categoryResults = useStore((state) => state.results[selectedCategory]);
  const loadingMoreResults = useStore((state) => state.moreResultsAreGenerating);
  const showResultsHeading = (categoryResults || loading) && showLoadingResults;

  const hasGoodResults =
    categoryResults && categoryResults.filter((res) => !res.isSensitive).length > 0;

  const classes = useStyles({ loading });

  return (
    <div className={classes.root} id="qt_copywriter_results">
      {showResultsHeading && (
        <Text
          size={Typography.t6}
          weight={FontWeights.Bold}
          elementType={TextElementTypes.H2}
          className={classes.label}
        >
          {t('editor.results.label')}
        </Text>
      )}

      <>
        {loading ? (
          showLoadingResults && <LoadingResults />
        ) : (
          <>
            {categoryResults &&
              (hasGoodResults ? (
                <>
                  <div className={classes.results}>
                    {categoryResults.map((item, index) => (
                      <Result
                        index={index}
                        id={item.id}
                        key={item.id}
                        type={item.type}
                        markdown={item.result}
                        rephrase={rephrase}
                        category={selectedCategory}
                        canBeSavedToRecent={canBeSavedToRecents}
                        rephrasedResults={item.rephrasedResults}
                        isRephrasing={item.isRephrasing}
                        showWordCount
                      />
                    ))}
                  </div>

                  {hideGenerateMoreBtn ? null : (
                    <div className={classes.buttonContainer}>
                      <Button
                        isFullWidth
                        onClick={generateMoreResults}
                        variant={ButtonVariants.Outline}
                        size={ButtonSizeXL}
                        skin={ButtonSkinSecondary}
                        isDisabled={loadingMoreResults}
                        isLoading={loadingMoreResults}
                      >
                        {t('editor.results.generateMoreButton')}
                      </Button>
                    </div>
                  )}
                </>
              ) : (
                <NoResults />
              ))}
          </>
        )}
      </>
    </div>
  );
}
export default Results;
